import { useEffect, useState } from "react";
import { LuLoaderCircle } from "react-icons/lu";
import {
  ExportStatus,
  useProjectExportsActions,
  useProjectExportStatus,
  useProjectLatestExport,
} from "../../../core_stores/useProjectExportsStore";
import { useUserSubscription } from "../../../core_stores/useSubscriptionStore";
import { HttpNotEnoughTokensException } from "../../../model/httpException";
import { useSnackbar } from "../../../providers/SnackbarProvider";
import BuildProgressBar from "../../common_components/BuildProgressBar";

export default function ProjectExportStartExport() {
  const latestExport = useProjectLatestExport();
  const exportStatus = useProjectExportStatus();
  const userSubscription = useUserSubscription();

  const { startExport } = useProjectExportsActions();
  const { showSnackbar } = useSnackbar();

  const [isLoading, setIsLoading] = useState(false);

  const isExporting = exportStatus === ExportStatus.EXPORTING;
  const canExport = userSubscription?.plan !== "Dream";

  useEffect(() => {
    if (isExporting && !isLoading) {
      setIsLoading(true);
    } else if (!isExporting && isLoading) {
      setIsLoading(false);
    }
  }, [isExporting]);

  const handleClick = async () => {
    setIsLoading(true);
    try {
      await startExport();
    } catch (error) {
      if (error instanceof HttpNotEnoughTokensException) {
        showSnackbar("You don't have enough tokens to export your project.", {
          type: "error",
        });
        setIsLoading(false);
      }
    }
  };

  const getDescription = () => {
    if (isLoading) {
      return "Export in progress\nEstimated time 1-2 minutes.\nYou can return here when it's ready.";
    }
    return "Export project";
  };

  return (
    <div
      className="flex flex-col items-center justify-center 
      border rounded-xl border-white dark:border-[#373B40]
      py-[50px] mr-2 gap-4 relative"
    >
      <span className="max-w-[300px] text-center whitespace-pre-line">
        {getDescription()}
      </span>
      {isExporting ? (
        <BuildProgressBar
          buildCreationTimestamp={latestExport?.creationTimestamp}
          estimatedBuildDurationInSeconds={120}
        />
      ) : (
        <Button
          label={"Export"}
          isLoading={isLoading}
          isDisabled={isLoading || !canExport}
          handleClick={handleClick}
        />
      )}
    </div>
  );
}

function Button({
  label,
  isLoading,
  isDisabled,
  handleClick,
}: {
  label: string;
  isLoading: boolean;
  isDisabled: boolean;
  handleClick: () => void;
}) {
  return (
    <button
      id="export-project-start-export"
      className="bg-primary-blue text-white hover:brightness-150 transition-all duration-300
          w-full max-w-[250px]
          flex items-center justify-center
          py-2 px-4
          rounded-full overflow-hidden
          border border-white dark:border-[#373B40]"
      onClick={handleClick}
      disabled={isDisabled}
    >
      {isLoading ? (
        <LuLoaderCircle className="animate-spin" size={20} />
      ) : (
        label
      )}
    </button>
  );
}
